import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { extendInterviewExpiration } from '../../services/interview.service';
import { useAuth0 } from '@auth0/auth0-react';
import {
  format,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isBefore,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  differenceInDays,
  isWithinInterval
} from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/atoms/Button';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatCalendarMonthYear, formatDate } from '@/utils/date';

interface ExtendInterviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  interviewId: string;
  candidateName: string;
  currentExpiration: string | null;
  onSuccess?: () => void;
}

export const ExtendInterviewModal: React.FC<ExtendInterviewModalProps> = ({
  isOpen,
  onClose,
  interviewId,
  candidateName,
  currentExpiration,
  onSuccess
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { t, i18n: { language: uiLanguage } } = useTranslation(['job', 'common']);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const weekDays = t('extend_interview_dialog.weekdays', {
    returnObjects: true,
  }) as string[];

  const monthLabel = useMemo(
    () => formatCalendarMonthYear(currentMonth),
    [currentMonth, uiLanguage],
  );

  // Reset selected date when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedDate(null);
    }
  }, [isOpen]);

  // Get today's date at start of day to avoid timezone issues
  const getToday = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  };
  const currentExpirationDate = useMemo(() =>
    currentExpiration ? parseISO(currentExpiration) : null,
    [currentExpiration]
  );

  // Check if current expiration is expired
  const isExpired = useMemo(() =>
    currentExpirationDate ? isBefore(currentExpirationDate, getToday()) : false,
    [currentExpirationDate]
  );

  // Generate calendar days for the current month view
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    return eachDayOfInterval({ start: startDate, end: endDate });
  }, [currentMonth]);

  const handleDateSelect = (date: Date) => {
    const today = getToday();
    // Normalize selected date to end of day
    const normalizedDate = new Date(date);
    normalizedDate.setHours(23, 59, 59, 999);
    
    // For comparison, we need to compare at start of day
    const dateForComparison = new Date(date);
    dateForComparison.setHours(0, 0, 0, 0);
    
    // Can only select dates after the current expiration (if not expired) or after today (if expired)
    const minSelectableDate = currentExpirationDate && !isExpired ? currentExpirationDate : today;
    
    if (isBefore(dateForComparison, minSelectableDate) || isSameDay(dateForComparison, minSelectableDate)) {
      toast.error(
        isExpired
          ? t('extend_interview_dialog.error_select_after_today')
          : t('extend_interview_dialog.error_select_after_deadline')
      );
      return;
    }

    setSelectedDate(normalizedDate);
  };

  const handleSubmit = async () => {
    if (!selectedDate) {
      toast.error(t('extend_interview_dialog.error_select_deadline'));
      return;
    }

    // Backend adds days to today's date, so always calculate from today
    const days = differenceInDays(selectedDate, getToday());
 
    if (days < 1) {
      toast.error(t('extend_interview_dialog.error_after_today'));
      return;
    }
 
    if (days > 365) {
      toast.error(t('extend_interview_dialog.error_max_days'));
      return;
    }

    setIsSubmitting(true);
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await extendInterviewExpiration(accessToken, interviewId, days);

      if (response.error) {
        throw new Error(response.error as string);
      }

      toast.success(
        t('extend_interview_dialog.success', {
          date: formatDate(selectedDate),
          name: candidateName,
        })
      );
      onSuccess?.();
      onClose();
    } catch (error) {
      const fallback = t('extend_interview_dialog.failed');
      const errorMessage =
        error instanceof Error && error.message.trim() ? error.message : fallback;
      toast.error(errorMessage);
      console.error('Error extending interview deadline:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDayClassName = (date: Date) => {
    const baseClasses = 'h-10 aspect-square rounded-full flex items-center justify-center text-sm font-body transition-colors';
    const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
    const today = getToday();

    // Current expiration date
    if (currentExpirationDate && isSameDay(date, currentExpirationDate)) {
      return `${baseClasses} ${isExpired ? 'bg-red-500 text-white font-bold' : 'bg-green-500 text-white font-bold'}`;
    }

    // Days between today and current expiration (if not expired)
    if (currentExpirationDate && !isExpired && isWithinInterval(date, {
      start: today < currentExpirationDate ? today : currentExpirationDate,
      end: today > currentExpirationDate ? today : currentExpirationDate
    }) && !isSameDay(date, today) && !isSameDay(date, currentExpirationDate)) {
      return `${baseClasses} bg-transparent text-dark-invirtus font-semibold`;
    }

    // Today
    if (isSameDay(date, today)) {
      return `${baseClasses} border-2 border-purple ${isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}`;
    }

    // Selected date
    if (selectedDate && isSameDay(date, selectedDate)) {
      return `${baseClasses} bg-purple text-dark-invirtus font-bold`;
    }

    // Days in extension range (between today and selected date)
    if (selectedDate) {
      const startDate = today < selectedDate ? today : selectedDate;
      const endDate = today > selectedDate ? today : selectedDate;
      if (isWithinInterval(date, { start: startDate, end: endDate }) &&
          !isSameDay(date, today) &&
          !isSameDay(date, selectedDate)) {
        return `${baseClasses} bg-purple/20 text-dark-invirtus font-semibold`;
      }
    }

    // Disabled dates (before current expiration if active, or before today if expired)
    const minSelectableDate = currentExpirationDate && !isExpired ? currentExpirationDate : today;
    if (isBefore(date, minSelectableDate) || isSameDay(date, minSelectableDate)) {
      return `${baseClasses} text-gray-300 cursor-not-allowed`;
    }

    // Selectable dates
    if (isCurrentMonth) {
      return `${baseClasses} hover:bg-purple/10 cursor-pointer text-gray-900`;
    }

    // Other month days
    return `${baseClasses} ${isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}`;
  };

  if (!isOpen) return null;


  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h5>{t('extend_interview_dialog.title')}</h5>
          <Button
            type="icon"
            intent="muted"
            onClick={onClose}
            icon={<X/>}
            label={t('extend_interview_dialog.close')}
          />
        </div>

        {/* Candidate Info */}
        <div className="mb-4">
          <p className='font-body text-sm'>
            <span className="text-dark-invirtus/75">{t('extend_interview_dialog.candidate')}</span> {candidateName}
          </p>
          <p className="font-body text-sm">
            <span className="text-dark-invirtus/75">{t('extend_interview_dialog.current_deadline')}</span>{' '}
            {currentExpirationDate ? formatDate(currentExpirationDate) : t('common:dates.not_available')}
          </p>
        </div>

        {/* Calendar */}

        <div className="mb-6">
          <div className="bg-light rounded-lg p-4 border border-gray-200">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                type="button"
              >
                <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              <h6>{monthLabel}</h6>
              <button
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                type="button"
              >
                <ChevronRightIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {/* Week Days Header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map((day) => (
                <div key={day} className="h-10 flex items-center justify-center">
                  <span className="text-xs font-semibold text-gray-600 font-body">{day}</span>
                </div>
              ))}
            </div>

            {/* Calendar Days Grid */}
            <div className="grid grid-cols-7 gap-2 place-content-center place-items-center">
              {calendarDays.map((day, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleDateSelect(day)}
                  className={getDayClassName(day)}
                >
                  {format(day, 'd')}
                </button>
              ))}
            </div>
          </div>

          {/* Selected Date Display */}
          {selectedDate && (
            <div className="mt-4 p-3 bg-purple/10 rounded-lg border border-purple">
              <p className="text-sm font-body">
                <span className="font-semibold text-purple">{t('extend_interview_dialog.new_deadline')}</span>{' '}
                {formatDate(selectedDate)}
              </p>
            </div>
          )}
        </div>


        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <Button
            label={t('extend_interview_dialog.cancel')}
            onClick={onClose}
            type="outlined"
            intent="secondary"
          />
          <Button
            label={t('extend_interview_dialog.confirm')}
            onClick={handleSubmit}
            type="contained"
            intent="secondary"
            disabled={!selectedDate}
            loading={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};
